import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ExpandMore,
  Edit,
  Delete,
  Add,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { ProgramVersionDto, ProgramVersionItemDto } from '../../../api/types/dtos/program';
import {
  useProgramVersionItems,
  useDeleteProgramVersionItem,
} from '../../../api/hooks/useProgramVersionItems';
import { useOrganization } from '../../../contexts/OrganizationContext';
import VersionItemCard from './VersionItemCard';
import SequenceControls from '../../../pages/trainings/components/SequenceControls';
import EditVersionModal from '../modals/EditVersionModal';
import CreateVersionItemModal from '../modals/CreateVersionItemModal';
import { canMoveUp, canMoveDown } from '../utils/sequenceHelpers';

interface VersionAccordionProps {
  version: ProgramVersionDto;
  allVersions: ProgramVersionDto[];
  programId: string;
  onMoveUp: (version: ProgramVersionDto) => void;
  onMoveDown: (version: ProgramVersionDto) => void;
  onEditVersion: (version: ProgramVersionDto) => void;
}

const VersionAccordion: React.FC<VersionAccordionProps> = ({
  version,
  allVersions,
  programId,
  onMoveUp,
  onMoveDown,

}) => {
  const [expanded, setExpanded] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createItemModalOpen, setCreateItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ProgramVersionItemDto | null>(null);
  
  const { selectedOrganization } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();

  const { data: items = [], isLoading: itemsLoading } = useProgramVersionItems(
    selectedOrganization?.id || '',
    programId,
    version.id,
    true
  );

  const deleteItem = useDeleteProgramVersionItem(
    selectedOrganization?.id || '',
    programId,
    version.id
  );

  const handleDeleteVersion = async () => {
    // Note: Delete version functionality would be handled by parent component
    setDeleteDialogOpen(false);
    enqueueSnackbar('Eliminar versión debe ser manejado desde el componente padre', { variant: 'info' });
  };

  const handleDeleteItem = async (itemId: string) => {
    try {
      await deleteItem.mutateAsync(itemId);
      enqueueSnackbar('Item eliminado exitosamente', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error al eliminar el item', { variant: 'error' });
      console.error('Error deleting item:', error);
    }
  };

  const handleEditItem = (item: ProgramVersionItemDto) => {
    setEditingItem(item);
  };

  const handleCloseEditItem = () => {
    setEditingItem(null);
  };

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        sx={{
          mb: 2,
          border: '2px solid',
          borderColor: 'primary.light',
          borderRadius: '8px !important',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: 'primary.light',
            color: 'primary.contrastText',
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
              justifyContent: 'space-between',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <Typography variant="h6">{version.name}</Typography>
            <Chip
              label={`${items.length} items`}
              size="small"
              sx={{ bgcolor: 'white', color: 'primary.main' }}
            />
          </Box>
          <Box
            sx={{ display: 'flex', gap: 0.5 }}
            onClick={(e) => e.stopPropagation()}
          >
            <SequenceControls
              canMoveUp={canMoveUp(version, allVersions)}
              canMoveDown={canMoveDown(version, allVersions)}
              onMoveUp={() => onMoveUp(version)}
              onMoveDown={() => onMoveDown(version)}
            />
            <Tooltip title="Editar">
              <IconButton
                size="small"
                onClick={() => setEditModalOpen(true)}
                sx={{ color: 'white' }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar">
              <IconButton
                size="small"
                onClick={() => setDeleteDialogOpen(true)}
                sx={{ color: 'white' }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3 }}>
          {version.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {version.description}
            </Typography>
          )}
          {version.documentUrl && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Documento:</strong> <a href={version.documentUrl} target="_blank" rel="noopener noreferrer">{version.documentUrl}</a>
            </Typography>
          )}
          {version.website && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Sitio web:</strong> <a href={version.website} target="_blank" rel="noopener noreferrer">{version.website}</a>
            </Typography>
          )}

          {itemsLoading ? (
            <Typography>Cargando items...</Typography>
          ) : (
            <>
              {items.map((item) => (
                <VersionItemCard
                  key={item.id}
                  item={item}
                  allItems={items}
                  onEdit={handleEditItem}
                  onDelete={handleDeleteItem}
                />
              ))}

              <Button
                variant="contained"
                startIcon={<Add />}
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => setCreateItemModalOpen(true)}
              >
                Agregar Item
              </Button>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      <EditVersionModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        version={version}
        programId={programId}
      />

      <CreateVersionItemModal
        open={createItemModalOpen}
        onClose={() => setCreateItemModalOpen(false)}
        programId={programId}
        versionId={version.id}
        existingItems={items}
      />

      {editingItem && (
        <CreateVersionItemModal
          open={!!editingItem}
          onClose={handleCloseEditItem}
          programId={programId}
          versionId={version.id}
          existingItems={items}
          editingItem={editingItem}
        />
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro que desea eliminar la versión &quot;{version.name}&quot;? Esto también
            eliminará todos los items asociados. Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDeleteVersion} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VersionAccordion;

